// @flow
import * as React from 'react';

import { useDispatch } from 'react-redux';

import { View } from 'reshaped';

import {
  selectCartProductOwner,
  selectCartReselleeID,
  selectHasOwnerOnly,
  selectCanChooseResellee,
} from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import { selectCurrentId } from '@gandi/redux-sharing';
import { Button } from '@gandi/reshaped';
import type { ButtonProps } from '@gandi/reshaped';
import { useTranslator } from '@gandi/translate';

import { logging } from 'gandi.redux-logger';

import { CUSTOMER } from 'src/Cart/AccountChoice/components/ResellerOwnerType';
import { mustCartOwnerBeReseller } from 'src/Cart/AccountChoice/modules/helpers';
import {
  selectCanChooseProductOwner,
  selectCartOwners,
  selectCurrentOrganizationCanPay,
} from 'src/Cart/AccountChoice/modules/selectors';
import { selectHasDomainTransfer } from 'src/Cart/Setup/modules/selectors';
import { useAppSelector } from 'src/hooks/useAppSelector';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import {
  selectShopCurrent,
  selectHasMissingOrgInfo,
} from 'src/modules/organization/selectors';

import { useCartSummaryContext } from '../../../context/CartSummary';
import { useModalContext } from '../../../context/Modal';
import { useButtonDisabledReason } from '../hooks/useButtonDisabledReason';

import { CartSummaryPriceBreakdown } from './CartSummaryPriceBreakdown';
import { useAccountChoiceSubmit } from './modules/useAccountChoiceSubmit';
import { useRoutingAfterAccountChoice } from './modules/useRoutingAfterAccountChoice';

function CurrentButton({
  submitting,
  disabled,
  handleClick,
  driveUserToSetup,
}: {
  submitting: boolean,
  disabled: boolean,
  handleClick: ButtonProps['onClick'],
  driveUserToSetup: boolean,
}) {
  const { __ } = useTranslator();

  return (
    <Button
      size="xlarge"
      color="primary"
      fullWidth
      loading={submitting}
      disabled={disabled}
      // Bypass hydration warning for Reshaped Button
      // (React sees the disabled prop as `null` instead of `true`, during initial SSR render,
      // but it correctly behaves as a disabled button even before hydration)
      attributes={{
        suppressHydrationWarning: true,
      }}
      onClick={handleClick}
    >
      {driveUserToSetup
        ? __('Continue')
        : __(
            '<for-translators-use>Translate to "Continue"</for-translators-use>Checkout',
          )}
    </Button>
  );
}

export function ContinueButton(): React.MixedElement | null {
  const dispatch = useDispatch();
  const translator = useTranslator();

  const displayNewAccountChoice = useFeatureFlag('FF_SHOP_ACCOUNT_CHOICE');

  const {
    frozen,
    isReseller,
    ownerType,
    setFrozen,
    setSubmitting,
    submitting,
  } = useCartSummaryContext();

  const { __ } = translator;

  const logger = logging(dispatch, 'AccountChoice');

  // WARN: we pass false to selectShopCurrent second param
  // because on AccountChoice at least
  // being authenticated does NOT mean that the current orga is loaded
  const currentShopOrganization = useAppSelector(state =>
    selectShopCurrent(state, false),
  );

  const sharingId = useAppSelector(selectCurrentId);
  const reseller = currentShopOrganization?.reseller;

  const productOwner = useAppSelector(selectCartProductOwner);
  const missingCartOwnerInfo = useAppSelector(state => {
    return selectHasMissingOrgInfo(state, { orgId: sharingId });
  });

  const missingProductOwnerInfo = useAppSelector(state => {
    return (
      !!selectHasMissingOrgInfo(state, { orgId: productOwner }) && productOwner
    );
  });

  const canPay = useAppSelector(selectCurrentOrganizationCanPay);
  const resellee = useAppSelector(selectCartReselleeID) || undefined;
  const cartOwners = useAppSelector(selectCartOwners);

  const hasDomainTransfer = useAppSelector(selectHasDomainTransfer);

  const hasOwnerOnly = useAppSelector(selectHasOwnerOnly);
  // $FlowFixMe PartialOrganization should be compatible with CartOwnerCandidate
  const ownerMustReseller = mustCartOwnerBeReseller(hasOwnerOnly, cartOwners);

  const chooseOwner = useAppSelector(selectCanChooseProductOwner);
  const chooseResellee = useAppSelector(state => {
    return reseller && selectCanChooseResellee(state);
  });

  const { openModal } = useModalContext();

  const missingInfo = missingCartOwnerInfo || missingProductOwnerInfo;

  const driveUserToSetup = !missingInfo && hasDomainTransfer;

  const accountChoiceSubmit = useAccountChoiceSubmit();
  const routingAfterAccountChoice = useRoutingAfterAccountChoice();

  React.useEffect(() => {
    setFrozen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // For new feature
  const { isDisabled } = useButtonDisabledReason();

  const disabled =
    frozen ||
    (displayNewAccountChoice
      ? isDisabled
      : !canPay ||
        (ownerMustReseller && !reseller) ||
        (!!(chooseResellee && !chooseOwner && reseller) && !resellee) ||
        !!(reseller && ownerType === CUSTOMER && !resellee)) ||
    submitting;

  async function handleClick(e: SyntheticEvent<>) {
    if (missingInfo) {
      // Show "missing info" organizations; and let the user edit them or choose another organization.
      if (missingCartOwnerInfo)
        openModal({
          type: 'invoiceSelectModal',
          replace: true,
        });
      else if (missingProductOwnerInfo)
        openModal({
          type: isReseller ? 'customerSelectModal' : 'ownerSelectModal',
          replace: true,
        });
    } else {
      try {
        e.preventDefault();
        setFrozen(true);
        setSubmitting(true);
        await accountChoiceSubmit();
        await routingAfterAccountChoice();
      } catch (err) {
        setFrozen(false);
        setSubmitting(false);
        dispatch(notifyDanger(__('Unexpected error occurred')));
        logger.error({ err }, 'Unable to update products payload.');
      }
    }
  }

  return (
    <View width="100%" gap={4}>
      <CartSummaryPriceBreakdown />
      <CurrentButton
        submitting={submitting}
        disabled={disabled}
        handleClick={handleClick}
        driveUserToSetup={driveUserToSetup}
      />
    </View>
  );
}
